import * as React from "react";
import { useState } from "react";
import { CoachMarkerSetJourneyStatus } from "../../../Wrap/Models/Models/CoachMarkers/CoachMarkerSetJourneyStatus";
import { CoachMarkerSetType } from "../../../Wrap/Models/Models/CoachMarkers/CoachMarkerSetType";
import { CoachMarkerSetJourneyStateForClientClient } from "../../../Wrap/Api/CoachMarkers/CoachMarkerSetJourneyStateForClient";
import { CoachMarkerSetJourneyStatusesByClientResponse } from "../../../Wrap/Models/Models/CoachMarkers/CoachMarkerSetJourneyStatusesByClientResponse";
import { CoachMarkerStepPosition } from "./CoachMarker";

type CoachMarkerStep = {
    stepKey: string;
    title: string;
    description: JSX.Element;
    stepPosition: CoachMarkerStepPosition;
    positionTop: number;
    positionLeft: number;
    onClick?: () => void;
};

export interface ICoachMarkerPageConfig {
    coachMarkerSetType: CoachMarkerSetType;
    steps: CoachMarkerStep[];
}

interface ICoachMarkerContextState {
    setPageConfig: (ICoachMarkerPageConfig) => void;
    step: CoachMarkerStep;
    nextStep: () => void;
    showCoachMarks: boolean;
    reset: () => void;
    coachMarkerSetJourneyStatuses?: { [key: string]: CoachMarkerSetJourneyStatus };
    onClickDoNotShow?: () => void;
    dotBarDots: JSX.Element;
    buttonText: string;
    hasUnread: boolean;
}

interface ICoachMarkerProviderProps {
    children: React.ReactNode;
}

export const CoachMarkerContext = React.createContext<ICoachMarkerContextState>(null);

const CoachMarkerProvider = (props: ICoachMarkerProviderProps) => {
    const [pageConfig, setPageConfig] = useState(null);
    const [activeStepNumber, setActiveStepNumber] = useState(0);
    const [showCoachMarks, setShowCoachMarks] = useState(false);
    const [doNotShow, setDoNotShow] = useState(true);

    React.useEffect(() => {
        setDoNotShow(true);
        if (!pageConfig) {
            // Set context after other async process is done, i.e. RoutingActionsStatic.navigateTo
            setTimeout(() => setShowCoachMarks(false), 0);
        }
        setActiveStepNumber(0);
        pageConfig && getAndSetInitialCoachMarkerSetJourneyStatuses();
    }, [pageConfig]);

    const DotBarDots = () => {
        return (
            <span className={`dots-bar step-${activeStepNumber + 1}`} aria-hidden={true}>
                {pageConfig.steps.map((step, i) => {
                    return <span key={`coachDots${i}`} className={`dot-${i + 1}`} />;
                })}
            </span>
        );
    };

    const nextStep = () => {
        const nextStepNumber = activeStepNumber + 1;
        if (pageConfig && pageConfig.steps.filter((a) => a.title != "App Store").length === nextStepNumber) {
            setCoachMarkerSetJourneyInitialised();
        } else {
            setActiveStepNumber(nextStepNumber);
        }
    };

    return (
        <CoachMarkerContext.Provider
            value={{
                setPageConfig,
                step: pageConfig && pageConfig.steps[activeStepNumber],
                nextStep: pageConfig && nextStep,
                showCoachMarks: !doNotShow,
                hasUnread: showCoachMarks,
                onClickDoNotShow: () => {
                    setDoNotShow(true);
                    localStorage.setItem("doNotShowCoachMarker", JSON.stringify(true));
                    //setShowCoachMarks(false);
                    setActiveStepNumber(0);
                },
                reset: () => {
                    setDoNotShow(false);
                    setActiveStepNumber(0);
                },
                dotBarDots: pageConfig && <DotBarDots />,
                buttonText:
                    pageConfig && pageConfig.steps.filter((a) => a.title != "App Store").length - 1 === activeStepNumber
                        ? "Got it"
                        : "Next",
            }}
        >
            {props.children}
        </CoachMarkerContext.Provider>
    );

    function getAndSetInitialCoachMarkerSetJourneyStatuses() {
        const client = new CoachMarkerSetJourneyStateForClientClient();
        const promise = client.get();

        promise.onSuccess((response) => {
            getShowCoachMarksFromStatuses(response);
        });
    }

    function setCoachMarkerSetJourneyInitialised() {
        const client = new CoachMarkerSetJourneyStateForClientClient();
        const promise = client.put({
            CoachMarkerSetType: pageConfig.coachMarkerSetType,
            CoachMarkerSetJourneyStatus: CoachMarkerSetJourneyStatus.Initialised,
        });

        promise.onSuccess((response) => {
            getShowCoachMarksFromStatuses(response);
        });
    }

    function getShowCoachMarksFromStatuses(response: CoachMarkerSetJourneyStatusesByClientResponse) {
        const thisJourney = response.CoachMarkerSetJourneys.find((journey) => {
            return journey.CoachMarkerSetType === pageConfig.coachMarkerSetType;
        });
        const doNotShowStored = localStorage.getItem("doNotShowCoachMarker")
            ? JSON.parse(localStorage.getItem("doNotShowCoachMarker"))
            : false;
        if (!thisJourney) {
            setShowCoachMarks(true);
            setDoNotShow(doNotShowStored);
        } else {
            setShowCoachMarks(thisJourney.CoachMarkerSetJourneyStatus !== CoachMarkerSetJourneyStatus.Initialised);
            setDoNotShow(
                doNotShowStored || thisJourney.CoachMarkerSetJourneyStatus == CoachMarkerSetJourneyStatus.Initialised
            );
        }
    }
};
export default React.memo(CoachMarkerProvider);
